import { PropsWithChildren, useRef, useState } from 'react';
import { CampaignFilterOperator, ServiceCategory } from '@prisma/client';
import {
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { toast } from 'react-toastify';
import { CampaignFilter } from '../../../../types/marketing';
import SingleSelectControlled from '../../../form/base/SingleSelectControlled';
import TextInputControlled from '../../../form/base/TextInputControlled';
import { getBusinessCurrency } from '../../../../helpers/businesses';
import useUser from '../../../../hooks/global/useUser';
import { getFullName } from '../../../../helpers/global';
import {
  convertSnakeToTitleCase,
  important,
} from '../../../../utils/global/helper';
import useGetTags from '../../../../hooks/flashbooks/useGetTags';

interface InputsProps {
  filter: CampaignFilter;
  onChange: (filter: Partial<CampaignFilter>) => void;
}

export const inputContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
};

function Text({ children: text }: PropsWithChildren) {
  return (
    <Typography
      variant="body2"
      sx={{
        color: 'text.primary',
      }}
      whiteSpace="nowrap">
      {text}
    </Typography>
  );
}

export function NumberOperatorInput({ filter, onChange }: InputsProps) {
  return (
    <SingleSelectControlled
      id="operator"
      options={[
        { value: 'GT', label: 'is greater than' },
        { value: 'LT', label: 'is less than' },
      ]}
      onChange={value => {
        onChange({
          ...filter,
          operator: value as CampaignFilterOperator,
        });
      }}
      value={filter.operator}
      autoWidth={false}
    />
  );
}

export function NumberInput({ filter, onChange }: InputsProps) {
  return (
    <TextInputControlled
      id="value"
      placeholder="Enter a value"
      type="number"
      value={filter.value.toString()}
      onChange={value => {
        onChange({
          value,
        });
      }}
      min={0}
      step={1}
      inputSx={{
        minWidth: 120,
      }}
    />
  );
}

export function SpendInputs({ filter, onChange }: InputsProps) {
  const { user } = useUser();

  const currency = user?.business ? getBusinessCurrency(user.business) : 'USD';

  return (
    <Box {...inputContainerStyle}>
      <NumberOperatorInput filter={filter} onChange={onChange} />
      <NumberInput filter={filter} onChange={onChange} />
      <Text>{currency}</Text>
    </Box>
  );
}

export function DateInputs({ filter, onChange }: InputsProps) {
  const operatorValue =
    filter.category === 'LAST_APPOINTMENT_WITHIN_DAYS'
      ? `within-${filter.operator}`
      : filter.operator;
  return (
    <Box {...inputContainerStyle}>
      <SingleSelectControlled
        id="operator"
        options={[
          { value: 'within-GT', label: 'is within' },
          { value: 'within-LT', label: 'is not within' },
          { value: 'GT', label: 'is after' },
          { value: 'LT', label: 'is before' },
        ]}
        onChange={value => {
          const category = value.startsWith('within')
            ? 'LAST_APPOINTMENT_WITHIN_DAYS'
            : 'LAST_APPOINTMENT_DATE';
          const operator = value.replace(
            'within-',
            '',
          ) as CampaignFilterOperator;
          const isNewCategory = category !== filter.category;
          onChange({
            ...filter,
            category,
            operator,
            value: isNewCategory ? '' : filter.value,
          } as CampaignFilter);
        }}
        value={operatorValue}
        autoWidth={false}
      />
      {filter.category === 'LAST_APPOINTMENT_DATE' && (
        <TextInputControlled
          id="value"
          placeholder="Enter a date"
          type="date"
          value={(filter.value || '') as string}
          onChange={value => {
            onChange({
              value: value as string,
            });
          }}
        />
      )}
      {filter.category === 'LAST_APPOINTMENT_WITHIN_DAYS' && (
        <>
          <Text>the last</Text>
          <NumberInput filter={filter} onChange={onChange} />
          <Text>days</Text>
        </>
      )}
    </Box>
  );
}

export function CountInputs({ filter, onChange }: InputsProps) {
  return (
    <Box {...inputContainerStyle}>
      <NumberOperatorInput filter={filter} onChange={onChange} />
      <NumberInput filter={filter} onChange={onChange} />
      <Text>visits</Text>
    </Box>
  );
}

export function EmployeeSelectInput({ filter, onChange }: InputsProps) {
  const { user } = useUser();
  const employees = user?.business?.employees?.map(e => ({
    label:
      e.displayName?.trim() ||
      getFullName({
        firstName: e.user?.firstName || '',
        lastName: e.user?.lastName || '',
      }),
    value: e.employeeId,
  }));

  return (
    <Box {...inputContainerStyle}>
      <Text>has ever been</Text>
      <SingleSelectControlled
        id="value"
        options={employees ?? []}
        onChange={value => {
          onChange({ value });
        }}
        value={filter.value as string}
        autoWidth={false}
        placeholder="Select a provider"
        selectSx={{
          maxWidth: 250,
        }}
      />
    </Box>
  );
}

export function AppointmentServiceInput({ filter, onChange }: InputsProps) {
  const options = Object.values(ServiceCategory).map(service => ({
    label: convertSnakeToTitleCase(service),
    value: service,
  }));
  return (
    <Box {...inputContainerStyle}>
      <Text>includes</Text>
      <SingleSelectControlled
        id="value"
        options={options ?? []}
        onChange={value => {
          onChange({ value });
        }}
        value={filter.value as string}
        autoWidth={false}
        placeholder="Select a service"
        selectSx={{
          maxWidth: 250,
        }}
      />
    </Box>
  );
}

export function VisitedOnInput({ filter, onChange }: InputsProps) {
  return (
    <Box {...inputContainerStyle}>
      <TextInputControlled
        id="value"
        placeholder="Select a date"
        type="date"
        value={(filter.value || '') as string}
        onChange={value => {
          onChange({ value });
        }}
      />
    </Box>
  );
}

export function FlashBookingInput({ filter, onChange }: InputsProps) {
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedTags = Array.isArray(filter.value)
    ? (filter.value as string[])
    : [];

  const { data: tags = [] } = useGetTags({ query, enabled: true });

  const allOptions = [...tags, ...selectedTags.filter(t => !tags.includes(t))];

  const renderValue = (selected: string[]) => {
    if (!selected.length) {
      return (
        <Typography component="em" variant="body2" color="text.tertiary">
          Any Tag
        </Typography>
      );
    }
    return (
      <Typography variant="body2" noWrap>
        {selected.join(', ')}
      </Typography>
    );
  };

  return (
    <Box {...inputContainerStyle}>
      <Text>has the tag(s)</Text>
      <Select
        multiple
        displayEmpty
        value={selectedTags}
        onChange={event => {
          onChange({ value: event.target.value as string[] });
        }}
        input={<OutlinedInput />}
        renderValue={selected => renderValue(selected as string[])}
        sx={{
          height: important('44px'),
          fontSize: 'body2.fontSize',
          width: 200,
          bgcolor: theme.palette.background.default,
        }}
        MenuProps={{
          autoFocus: false,
          MenuListProps: { autoFocusItem: false },
        }}
        onOpen={() => {
          setTimeout(() => searchRef.current?.focus(), 0);
        }}>
        <ListSubheader sx={{ pt: 1, pb: 0.5 }}>
          <TextField
            inputRef={searchRef}
            size="small"
            placeholder="Search tags…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
            fullWidth
            autoComplete="off"
            sx={{
              '& .MuiInputBase-root': {
                height: '44px',
                fontSize: 'body2.fontSize',
              },
            }}
          />
        </ListSubheader>
        {allOptions.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No tags found
            </Typography>
          </MenuItem>
        )}
        {allOptions.map(tag => (
          <MenuItem key={tag} value={tag} sx={{ gap: 1 }}>
            <Checkbox
              checked={selectedTags.includes(tag)}
              sx={{ padding: 0.5 }}
            />
            <ListItemText
              primary={tag}
              primaryTypographyProps={{ fontSize: 'body2.fontSize' }}
            />
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

export function TattooDescriptionInput({ filter, onChange }: InputsProps) {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState('');
  const words = Array.isArray(filter.value) ? (filter.value as string[]) : [];

  const addWord = () => {
    const word = inputValue.trim();
    if (!word) return;

    const isDuplicate = words.some(w => w.toLowerCase() === word.toLowerCase());

    if (isDuplicate) {
      toast.info(`"${word.toLowerCase()}" has already been added`);
    } else {
      onChange({ value: [...words, word] });
    }
    setInputValue('');
  };

  return (
    <Box {...inputContainerStyle}>
      <Text>contains the word(s)</Text>
      <OutlinedInput
        placeholder="Type a word"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addWord();
          }
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={addWord}
              disabled={!inputValue.trim()}
              edge="end"
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: 1,
                width: 28,
                height: 28,
                mr: 0.25,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                },
                '&.Mui-disabled': {
                  bgcolor: theme.palette.action.disabledBackground,
                },
              }}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        }
        sx={{
          height: important('44px'),
          fontSize: 'body2.fontSize',
          minWidth: 180,
          bgcolor: theme.palette.background.default,
        }}
      />
    </Box>
  );
}

export function TattooDescriptionChips({ filter, onChange }: InputsProps) {
  const theme = useTheme();
  const words = Array.isArray(filter.value) ? (filter.value as string[]) : [];

  if (!words.length) return null;

  const removeWord = (word: string) => {
    onChange({ value: words.filter(w => w !== word) });
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {words.map(word => (
        <Box
          key={word}
          onClick={() => removeWord(word)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: theme.palette.primary.main,
            borderRadius: '16px',
            px: 1.5,
            py: 0.5,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
            },
          }}>
          <Typography
            variant="subtitle2"
            color={theme.palette.primary.contrastText}>
            {word}
          </Typography>
          <CloseRounded
            sx={{
              width: 14,
              height: 14,
              color: theme.palette.primary.contrastText,
              opacity: 0.8,
            }}
          />
        </Box>
      ))}
    </Box>
  );
}
